import { Link, useParams } from 'react-router-dom';

import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useGetCaseThemes } from '@/api/queries/useGetCaseThemes';
import { Icon } from '@/new/components/icon';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/new/components/shadcn/accordion';
import { Button } from '@/new/components/shadcn/button';
import { Skeleton } from '@/new/components/shadcn/skeleton';
import { TextWithReferences } from '@/new/components/text-references';
import { Summary } from '@/types';

const ThemesOverviewLoader = () => {
  return (
    <div className="flex flex-col gap-3">
      {[...Array(2)].map((_, index) => (
        <div
          key={index}
          className="flex items-start gap-3 rounded-lg border border-blue-100 bg-blue-50/60 px-3 py-2 shadow-sm"
          style={{ borderLeft: '4px solid #3b82f6' }}
        >
          <Skeleton className="mt-1 h-4 w-4 bg-blue-200" />
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <Skeleton className="h-4 w-3/4 bg-blue-200" />
            <Skeleton className="h-3 w-full bg-blue-200" />
          </div>
        </div>
      ))}
    </div>
  );
};

interface ThemesOverviewProps {
  themes: Summary[];
  isLoading?: boolean;
}

export const ThemesOverview = ({ themes, isLoading = false }: ThemesOverviewProps) => {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return null;

  const { data: themesWithCounts = [] } = useGetCaseThemes(matterId);

  const themeCountMap = themesWithCounts.reduce<Record<string, number>>((acc, theme) => {
    acc[theme.name] = theme.count;
    return acc;
  }, {});

  if (!isLoading && !themes.length) {
    return null;
  }

  return (
    <div className="rounded-md border border-gray-200 bg-white">
      <div className="flex items-center gap-3 rounded-t-md border-b bg-gray-50 p-3 px-4">
        <Icon className="lucide--lightbulb h-4 w-4 text-gray-500" />
        <h3 className="text-sm font-medium text-gray-700">Issues</h3>
      </div>

      <div className="p-4 pt-2">
        {isLoading ? (
          <ThemesOverviewLoader />
        ) : (
          <Accordion type="multiple" className="flex flex-col">
            {themes.map((theme, index) => {
              const factCount = themeCountMap[theme.name] || 0;
              return (
                <AccordionItem key={index} value={`theme-${index}`}>
                  <AccordionTrigger
                    className="flex items-center justify-between border-b border-gray-100 pb-2"
                    onClick={() => {
                      trackCustomEvent(TrackingEvents.ViewFullThemesOnSummary);
                    }}
                  >
                    <div className="flex flex-1 items-center gap-3">
                      <Icon className="lucide--lightbulb h-4 w-4 shrink-0 text-blue-400" />
                      <div className="flex min-w-0 flex-1 flex-col text-left">{theme.name}</div>
                    </div>

                    {factCount > 0 && (
                      <Button asChild size="sm" className="mx-2 px-2 py-1">
                        <Link
                          to={`/matters/${matterId}/subsets/${subsetId}/facts?${new URLSearchParams({ themes: theme.name }).toString()}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span>
                            Explore <b>{factCount}</b> Related Facts
                          </span>
                          <Icon className="lucide--arrow-right h-3 w-3" />
                        </Link>
                      </Button>
                    )}
                  </AccordionTrigger>
                  <AccordionContent className="px-3 pt-0 pb-3">
                    <div className="mt-2 flex flex-col gap-3 leading-relaxed">
                      <TextWithReferences content={theme.content} />
                    </div>

                    {theme.subquestions && theme.subquestions.length > 0 && (
                      <>
                        <div className="mt-4 flex items-center gap-2 text-base font-medium text-gray-800 underline">
                          <Icon className="lucide--message-circle-question h-3 w-3 shrink-0" />
                          Related Questions
                        </div>

                        <Accordion type="multiple" className="flex flex-col">
                          {theme.subquestions.map((subquestion, subIndex) => (
                            <AccordionItem
                              key={subIndex}
                              value={`subquestion-${index}-${subIndex}`}
                              className="border-b border-gray-100"
                            >
                              <AccordionTrigger className="py-2 text-left text-sm hover:text-blue-500">
                                {subquestion.question}
                              </AccordionTrigger>
                              <AccordionContent className="pt-2 pb-4 text-sm leading-relaxed text-gray-700">
                                <TextWithReferences content={subquestion.answer} />
                              </AccordionContent>
                            </AccordionItem>
                          ))}
                        </Accordion>
                      </>
                    )}
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        )}
      </div>
    </div>
  );
};
